import { wsService } from './websocket';

interface LocationUpdate {
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  timestamp: number;
  rideId?: string;
}

interface PendingUpdate extends LocationUpdate {
  retryCount: number;
  lastRetry: number;
}

class LocationUpdateService {
  private pendingUpdates: Map<string, PendingUpdate> = new Map();
  private maxRetries = 3;
  private retryDelay = 2000;
  private acknowledgmentTimeout = 5000;
  private updateInterval: NodeJS.Timeout | null = null;
  private isTracking = false;

  startTracking(rideId?: string) {
    if (this.isTracking) {
      return;
    }

    this.isTracking = true;
    this.updateInterval = setInterval(() => {
      this.processPendingUpdates(rideId);
    }, 1000);
  }

  stopTracking() {
    this.isTracking = false;
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.pendingUpdates.clear();
  }

  async sendLocationUpdate(
    latitude: number,
    longitude: number,
    heading?: number,
    speed?: number,
    rideId?: string,
  ): Promise<void> {
    const update: LocationUpdate = {
      latitude,
      longitude,
      heading,
      speed,
      timestamp: Date.now(),
      rideId,
    };

    const updateKey = `${latitude.toFixed(6)}-${longitude.toFixed(6)}-${Date.now()}`;

    try {
      if (wsService.isConnected()) {
        wsService.emit('update_location', {
          latitude,
          longitude,
          heading,
          speed,
          rideId,
        });

        const pendingUpdate: PendingUpdate = {
          ...update,
          retryCount: 0,
          lastRetry: Date.now(),
        };

        this.pendingUpdates.set(updateKey, pendingUpdate);

        setTimeout(() => {
          if (this.pendingUpdates.has(updateKey)) {
            this.retryUpdate(updateKey, rideId);
          }
        }, this.acknowledgmentTimeout);
      } else {
        const pendingUpdate: PendingUpdate = {
          ...update,
          retryCount: 0,
          lastRetry: Date.now(),
        };
        this.pendingUpdates.set(updateKey, pendingUpdate);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error sending location update:', error);
      }
      const pendingUpdate: PendingUpdate = {
        ...update,
        retryCount: 0,
        lastRetry: Date.now(),
      };
      this.pendingUpdates.set(updateKey, pendingUpdate);
    }
  }

  acknowledgeLocationUpdate(latitude: number, longitude: number, timestamp: number) {
    const updateKey = Array.from(this.pendingUpdates.keys()).find((key) => {
      const update = this.pendingUpdates.get(key);
      return (
        update &&
        Math.abs(update.latitude - latitude) < 0.0001 &&
        Math.abs(update.longitude - longitude) < 0.0001 &&
        Math.abs(update.timestamp - timestamp) < 10000
      );
    });

    if (updateKey) {
      this.pendingUpdates.delete(updateKey);
    }
  }

  private retryUpdate(updateKey: string, rideId?: string) {
    const update = this.pendingUpdates.get(updateKey);
    if (!update) {
      return;
    }

    if (update.retryCount >= this.maxRetries) {
      this.pendingUpdates.delete(updateKey);
      if (import.meta.env.DEV) {
        console.warn('Location update failed after max retries:', updateKey);
      }
      return;
    }

    if (Date.now() - update.lastRetry < this.retryDelay) {
      return;
    }

    update.retryCount++;
    update.lastRetry = Date.now();

    if (wsService.isConnected()) {
      wsService.emit('update_location', {
        latitude: update.latitude,
        longitude: update.longitude,
        heading: update.heading,
        speed: update.speed,
        rideId: update.rideId || rideId,
      });

      setTimeout(() => {
        if (this.pendingUpdates.has(updateKey)) {
          this.retryUpdate(updateKey, rideId);
        }
      }, this.acknowledgmentTimeout);
    }
  }

  private processPendingUpdates(rideId?: string) {
    const now = Date.now();
    const staleThreshold = 30000;

    this.pendingUpdates.forEach((update, key) => {
      if (now - update.timestamp > staleThreshold) {
        this.pendingUpdates.delete(key);
        return;
      }

      if (now - update.lastRetry >= this.retryDelay && update.retryCount < this.maxRetries) {
        this.retryUpdate(key, rideId);
      }
    });
  }

  getPendingUpdatesCount(): number {
    return this.pendingUpdates.size;
  }
}

export const locationUpdateService = new LocationUpdateService();
